import React, { useState, useEffect } from "react";
import moment from "moment";
import closeIcon from "../../../../../../Assets/images/close-icon.svg";
import apiClient from "../../../../../../Config/apiClient";
import Loader from '../../../../../../Context/Loader/Loader';

const jobsBaseUrl = process.env.REACT_APP_TC_JOBS_BASE_PATH;

const AddEditNoteModal = ({
  show,
  onClose,
  onSave,
  initialData,
  jobId,
  allowJobSelection = false,
}) => {
  const [description, setDescription] = useState("");
  const [selectedJobId, setSelectedJobId] = useState(jobId || "");
  const [jobDetails, setJobDetails] = useState(null);
  const [allJobs, setAllJobs] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const fetchAllJobs = async () => {
    try {
      const res = await apiClient.get(`${jobsBaseUrl}/`);
      if (res.data.status === "SUCCESS") {
        setAllJobs(res.data.data || []);
      } else {
        setAllJobs([]);
      }
    } catch (err) {
      console.error("Error fetching jobs:", err);
      setAllJobs([]);
    }
  };

  const fetchJobDetails = async (id) => {
    if (!id) {
      setJobDetails(null);
      return;
    }
    try {
      const res = await apiClient.get(`${jobsBaseUrl}/get?id=${id}`);
      if (res.data.status === "SUCCESS") {
        setJobDetails(res.data.data);
      } else {
        setJobDetails(null);
      }
    } catch (err) {
      console.error("Error fetching job details:", err);
      setJobDetails(null);
    }
  };

  useEffect(() => {
    if (!show) {
      // Modal closed → reset state
      setSelectedJobId("");
      setJobDetails(null);
      return;
    }

    setSubmitted(false);
    setFormErrors({});

    if (initialData) {
      setDescription(initialData.description || "");
      setSelectedJobId(initialData.job_id || jobId || "");
    } else {
      setDescription("");
      setSelectedJobId("");
    }

    if (allowJobSelection) {
      fetchAllJobs();
    }

    if (!allowJobSelection && jobId) {
      fetchJobDetails(jobId);
    }
  }, [initialData, show, jobId, allowJobSelection]);

  useEffect(() => {
    if (allowJobSelection && selectedJobId) {
      fetchJobDetails(selectedJobId);
    }
  }, [selectedJobId, allowJobSelection]);

  const validateForm = () => {
    const errors = {};
    if (!description.trim()) {
      errors.description = "Note is required.";
    }
    if (allowJobSelection && !selectedJobId) {
      errors.jobId = "Please select a job.";
    }
    setFormErrors(errors);
    setSubmitted(true);
    return Object.keys(errors).length === 0;
  };

  const showError = (field) => submitted && formErrors[field];

  const handleSave = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      await onSave({ description, jobId: selectedJobId });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <div
      className="modal right rtm-modal fade show"
      style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div className="modal-dialog rtl-dialog">
        <div
          className="modal-content rtl-content vjob-content animate__animated animate__fadeInRight"
          style={{ maxHeight: "100vh", overflow: "hidden" }}
        >
          <div className="ajob-content" style={{ maxHeight: "70px", overflowY: "auto" }}>
            <div className="cpf-top">
              <h2 className="modal-heading">{initialData ? "Edit Note" : "Add Note"}</h2>
              <button className="m-close" onClick={onClose}>
                <img src={closeIcon} alt="close" />
              </button>
            </div>

            <div className="viewjob-inner">
              <div className="aptab-body">
                <div className="vr-inner">
                  <div className="vrt-content mt-0">
                    <div className="row">
                      {/* Note field */}
                      <div className="col-md-12">
                        <div className="cp-split" style={{ position: "relative" }}>
                          <label className="fi-label">
                            Note <span>*</span>
                          </label>
                          <textarea
                            name="description"
                            className={`common-text form-control ${showError("description") ? "error" : ""
                              }`}
                            maxLength="200"
                            placeholder="Write Notes Here"
                            value={description}
                            onChange={(e) => {
                              setDescription(e.target.value);
                              setFormErrors((prev) => ({ ...prev, description: "" }));
                            }}
                          />
                          {showError("description") && (
                            <span className="error-text active">
                              {formErrors.description}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Job selection or read-only */}
                      <div className="col-md-12">
                        <div className="cp-split">
                          <label className="fi-label">Job Details</label>
                          {allowJobSelection ? (
                            <>
                              <select
                                className={`form-control ${showError("jobId") ? "error" : ""
                                  }`}
                                value={selectedJobId}
                                onChange={(e) => {
                                  setSelectedJobId(e.target.value);
                                  setFormErrors((prev) => ({ ...prev, jobId: "" }));
                                }}
                              >
                                <option value="">-- Select Job --</option>
                                {allJobs.map((job) => (
                                  <option key={job.id} value={job.id}>
                                    {job.job_name} - {job.job_description}
                                  </option>
                                ))}
                              </select>
                              {showError("jobId") && (
                                <span className="error-text active">
                                  {formErrors.jobId}
                                </span>
                              )}
                            </>
                          ) : (
                            <input
                              type="text"
                              className="form-control"
                              disabled
                              value={
                                jobDetails
                                  ? `${jobDetails.job_name || ""} - ${jobDetails.job_description || ""
                                  }`
                                  : "N/A"
                              }
                            />
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Job details */}
                    {jobDetails && (
                      <>
                        <div className="row mt-2">
                          <div className="col-md-6">
                            <div className="vrt-split">
                              <h3>Start Location</h3>
                              <p>
                                {jobDetails.start_location ||
                                  (jobDetails.start_location_latitude &&
                                    jobDetails.start_location_longitude) ||
                                  "N/A"}
                              </p>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="vrt-split">
                              <h3>End Location</h3>
                              <p>
                                {jobDetails.end_location ||
                                  (jobDetails.end_location_latitude &&
                                    jobDetails.end_location_longitude) ||
                                  "N/A"}
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="row">
                          <div className="col-md-6">
                            <div className="vrt-split">
                              <h3>Start Time</h3>
                              <p>
                                {jobDetails.start_date_time
                                  ? moment.utc(jobDetails.start_date_time).local().format(
                                    "MMM DD, YYYY hh:mm A"
                                  )
                                  : "N/A"}
                              </p>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="vrt-split">
                              <h3>End Time</h3>
                              <p>
                                {jobDetails.end_date_time
                                  ? moment.utc(jobDetails.end_date_time).local().format(
                                    "MMM DD, YYYY hh:mm A"
                                  )
                                  : "N/A"}
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="row">
                          <div className="col-md-6">
                            <div className="vrt-split">
                              <h3>Supervisor</h3>
                              <p>
                                {jobDetails.cc_user
                                  ? `${jobDetails.cc_user.first_name} ${jobDetails.cc_user.last_name}`
                                  : "N/A"}
                              </p>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="vrt-split">
                              <h3>Contact No.</h3>
                              <p>{jobDetails.cc_user?.contact_no || "N/A"}</p>
                            </div>
                          </div>
                        </div>
                      </>
                    )}

                    {/* Save button */}
                    <div className="row mt-3">
                      <div className="col-md-6"></div>
                      <div className="col-md-6">
                        <div className="vrt-split">
                          <button
                            className="cp-button save-btn float-start"
                            onClick={handleSave}
                            disabled={loading}
                          >
                            {loading ? <Loader /> : "Save"}
                          </button>
                        </div>
                      </div>
                    </div>

                    {!jobDetails && selectedJobId && (
                      <div className="row mt-3">
                        <div className="col-md-12 text-center">
                          <p className="text-danger">Failed to load job details</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddEditNoteModal;
